import { useEffect, useRef, useState } from "react";
import { loadGoogleMapsPlacesApi } from "@app/lib/google-maps-places-loader";

type UsePlacesAutocompleteArgs = {
  inputElement: HTMLInputElement | null;
  onPlaceSelect: (selection: {
    place: google.maps.places.PlaceResult;
    inputValue: string;
  }) => void;
  autocompleteOptions?: google.maps.places.AutocompleteOptions;
  enabled?: boolean;
};

type UsePlacesAutocompleteResult = {
  isReady: boolean;
  apiError: string | null;
};

export function usePlacesAutocomplete({
  inputElement,
  onPlaceSelect,
  autocompleteOptions,
  enabled = true,
}: UsePlacesAutocompleteArgs): UsePlacesAutocompleteResult {
  const listenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const [isReady, setIsReady] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onPlaceSelect]);

  useEffect(() => {
    if (!enabled || !inputElement) {
      setIsReady(false);
      return;
    }

    let isCancelled = false;

    const setupAutocomplete = async () => {
      try {
        await loadGoogleMapsPlacesApi();

        if (isCancelled) {
          return;
        }

        const autocomplete = new window.google.maps.places.Autocomplete(
          inputElement,
          {
            fields: ["formatted_address", "name", "place_id"],
            types: ["address"],
            ...autocompleteOptions,
          },
        );

        listenerRef.current = autocomplete.addListener("place_changed", () => {
          onPlaceSelectRef.current({
            place: autocomplete.getPlace(),
            inputValue: inputElement.value,
          });
        });

        setApiError(null);
        setIsReady(true);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        setIsReady(false);
        setApiError(
          error instanceof Error
            ? error.message
            : "Unable to start Google Places autocomplete.",
        );
      }
    };

    void setupAutocomplete();

    return () => {
      isCancelled = true;
      setIsReady(false);
      listenerRef.current?.remove();
      listenerRef.current = null;
    };
  }, [autocompleteOptions, enabled, inputElement]);

  return {
    isReady,
    apiError,
  };
}
